import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authClient } from '@lib/betterAuth';
import { BrandAuthShell } from '@components/brand/BrandAuthShell';
import { BrandCard } from '@components/brand/BrandCard';

type Status = 'idle' | 'submitting' | 'sent';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === 'submitting') return;

    setStatus('submitting');

    try {
      await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: new URL('/reset-password', window.location.origin).href,
      });
      setStatus('sent');
    } catch (err: unknown) {
      console.error('Forget password request failed:', err);
      // Always show success to prevent email enumeration
      setStatus('sent');
    }
  };

  return (
    <BrandAuthShell>
      <BrandCard title={status === 'sent' ? t('auth.forgot.success') : t('auth.forgot.title')}>
        {status === 'sent' ? (
          <div role="status" className="fc-stack">
            <p className="fc-card-lead">{t('auth.forgot.successMessage')}</p>
            <Link to="/login">{t('auth.forgot.backToLogin')}</Link>
          </div>
        ) : (
          <form className="fc-stack" onSubmit={handleSubmit}>
            <p className="fc-card-lead">{t('auth.forgot.subtitle')}</p>
            <div className="fc-field">
              <label htmlFor="email">{t('auth.forgot.email')}</label>
              <input
                id="email"
                className="fc-input"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'submitting'}
                required
              />
            </div>
            <button type="submit" className="fc-button" disabled={status === 'submitting'}>
              {status === 'submitting' ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
            </button>
            <Link to="/login">{t('auth.forgot.backToLogin')}</Link>
          </form>
        )}
      </BrandCard>
    </BrandAuthShell>
  );
};

export default ForgotPassword;
